import React, { useMemo, useState } from 'react';
import {
    Box, Button, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, Typography,
    Radio, RadioGroup, FormControlLabel, Paper, Alert, Checkbox,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { apiConstants, postGeneric } from '../../../common/constants';
import { useAppointmentConfig } from '../shared/useAppointmentConfig';
import { CLOSURE_CATEGORY_META, getClosureCategoryMeta } from '../shared/constants';
import { formatDate1 } from '../../../common/util';
import { parseTime } from '../../../helpers/UtilityHelper';

export default function MarkMissedModal({ rowData, onClose, onDone, onScheduleNext }) {
    const { config } = useAppointmentConfig();
    const { enqueueSnackbar } = useSnackbar();
    const [category, setCategory] = useState('');
    const [reasonCode, setReasonCode] = useState('');
    const [remarks, setRemarks] = useState('');
    const [scheduleNext, setScheduleNext] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [touched, setTouched] = useState(false);

    const reasonsForCategory = useMemo(() => {
        const groups = config?.reasons || [];
        const match = groups.find((g) => g.category === category);
        return (match?.items || []).filter((i) => i.enabled !== false);
    }, [config, category]);

    const appt = rowData?.appointmentDeliveryData || {};
    const histCount = rowData?.appointmentDeliveryHistoryCount ?? rowData?.appointmentDeliveryHistory?.length ?? 0;
    const attemptNum = histCount + 1;

    const validate = () => {
        if (!category) return 'Select who was responsible.';
        if (!reasonCode) return 'Select a reason.';
        if (!remarks || remarks.trim().length < 10) return 'Remarks must be at least 10 characters.';
        return null;
    };

    const submit = async () => {
        setTouched(true);
        const err = validate();
        if (err) return;
        setSubmitting(true);
        try {
            await postGeneric(apiConstants.APPOINTMENT_DELIVERY_CLOSE, {
                awbNumber: rowData?.awb_number,
                closureReason: reasonCode,
                closureRemarks: remarks,
            });
            enqueueSnackbar('Appointment marked as missed', { variant: 'success' });
            onDone?.();
            if (scheduleNext) {
                onScheduleNext?.(rowData);
            }
            onClose?.();
        } catch (e) {
            enqueueSnackbar(e?.response?.data?.message || 'Failed to mark missed', { variant: 'error' });
        }
        setSubmitting(false);
    };

    const validationError = touched ? validate() : null;

    return (
        <Box>
            <Paper variant='outlined' sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant='caption' fontWeight={700} color='text.secondary'>CURRENT APPOINTMENT</Typography>
                <Typography variant='body2'><strong>Appointment #{attemptNum}</strong></Typography>
                <Typography variant='body2'>
                    Date: {appt.appointment_del_start ? formatDate1(appt.appointment_del_start, 'dd MMM, yyyy') : '-'}
                    {appt.appointment_del_end ? ` · ${parseTime(appt.appointment_del_start)} - ${parseTime(appt.appointment_del_end)}` : ''}
                </Typography>
                <Typography variant='body2'>
                    PO: {appt.appointment_del_po || '-'}
                    {appt.appointment_del_po_expiry ? ` · Expiry: ${formatDate1(appt.appointment_del_po_expiry, 'dd MMM, yyyy')}` : ''}
                </Typography>
            </Paper>

            <Typography variant='caption' fontWeight={700} color='primary'>STEP 1 — WHO WAS RESPONSIBLE? *</Typography>
            <RadioGroup
                value={category}
                onChange={(e) => { setCategory(e.target.value); setReasonCode(''); }}
                sx={{ mt: 1, mb: 2 }}
            >
                {CLOSURE_CATEGORY_META.map((m, i) => (
                    <Paper
                        key={m.value}
                        variant='outlined'
                        sx={{
                            p: 1.5, mb: i < CLOSURE_CATEGORY_META.length - 1 ? 1 : 0, cursor: 'pointer',
                            borderColor: category === m.value ? 'primary.main' : 'divider',
                            bgcolor: category === m.value ? '#e8eaf6' : 'transparent',
                        }}
                        onClick={() => { setCategory(m.value); setReasonCode(''); }}
                    >
                        <FormControlLabel
                            value={m.value}
                            control={<Radio />}
                            label={
                                <Box>
                                    <Typography variant='body2' fontWeight={600}>{m.choice}</Typography>
                                    <Typography variant='caption' color='text.secondary'>{m.choiceHint}</Typography>
                                </Box>
                            }
                        />
                    </Paper>
                ))}
            </RadioGroup>

            {category && (
                <>
                    <Typography variant='caption' fontWeight={700} color='primary'>STEP 2 — WHAT HAPPENED? *</Typography>
                    <FormControl fullWidth size='small' sx={{ mt: 1, mb: 2 }}>
                        <InputLabel>Select reason</InputLabel>
                        <Select
                            value={reasonCode}
                            label='Select reason'
                            onChange={(e) => setReasonCode(e.target.value)}
                            MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
                        >
                            {reasonsForCategory.length === 0 && <MenuItem disabled>No active reasons</MenuItem>}
                            {reasonsForCategory.map((r) => (
                                <MenuItem key={r.code} value={r.code} sx={{ whiteSpace: 'normal' }}>
                                    {r.label}
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>
                            {getClosureCategoryMeta(category)?.helper}
                        </FormHelperText>
                    </FormControl>
                </>
            )}

            {reasonCode && (
                <>
                    <Typography variant='caption' fontWeight={700} color='primary'>STEP 3 — ADD DETAILS *</Typography>
                    <Grid sx={{ mt: 1, mb: 1 }}>
                        <textarea
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            placeholder='Tell us more about what happened... (minimum 10 characters)'
                            style={{
                                width: '100%', minHeight: 80, padding: 10, fontSize: 13, borderRadius: 6,
                                border: '1px solid #ccc', fontFamily: 'inherit', resize: 'vertical',
                            }}
                        />
                        <Typography variant='caption' color='text.secondary'>
                            {remarks.length} / 10 min characters
                        </Typography>
                    </Grid>
                    <FormControlLabel
                        control={<Checkbox checked={scheduleNext} onChange={(e) => setScheduleNext(e.target.checked)} />}
                        label='Schedule next appointment right away'
                    />
                </>
            )}

            {validationError && (
                <Alert severity='error' sx={{ mt: 2 }}>{validationError}</Alert>
            )}

            <Box display='flex' justifyContent='flex-end' gap={1} sx={{ mt: 2 }}>
                <Button variant='outlined' size='small' onClick={onClose}>Cancel</Button>
                <LoadingButton
                    variant='contained'
                    color='warning'
                    size='small'
                    loading={submitting}
                    onClick={submit}
                >
                    Confirm & Mark as Missed
                </LoadingButton>
            </Box>
        </Box>
    );
}
